import { useCallback, useRef, useState } from "react";
import { motion } from "framer-motion";
import { ArrowLeftRight, Download, UploadCloud } from "lucide-react";
import { toast } from "sonner";
import { useBrokers } from "@/context/BrokersContext";
import { applySwapPatches, buildSwapsCatalog, downloadJson, parseSwapsUpload } from "@/lib/swapsCatalog";
import "./admin.css";

export default function AdminSwaps() {
  const { brokers, bulkUpsertBrokers } = useBrokers();
  const swapsInputRef = useRef(null);
  const [swapsDrag, setSwapsDrag] = useState(false);
  const [swapsPreview, setSwapsPreview] = useState(null);
  const [swapsError, setSwapsError] = useState("");
  const [swapsBusy, setSwapsBusy] = useState(false);

  const onSwapsFile = useCallback(async (file) => {
    if (!file) return;
    if (!file.name.toLowerCase().endsWith(".json") && file.type !== "application/json") {
      setSwapsError("Please upload a .json file.");
      setSwapsPreview(null);
      return;
    }
    const text = await file.text();
    const parsed = parseSwapsUpload(text);
    if (!parsed.ok) {
      setSwapsError(parsed.error);
      setSwapsPreview(null);
      return;
    }
    const applied = applySwapPatches(brokers, parsed.entries);
    if (!applied.updated.length) {
      setSwapsError(
        applied.missing.length
          ? `No matching brokers. Unknown slugs: ${applied.missing.join(", ")}`
          : "Nothing to update.",
      );
      setSwapsPreview(null);
      return;
    }
    setSwapsError(
      applied.missing.length
        ? `Will skip unknown slugs: ${applied.missing.join(", ")}`
        : "",
    );
    setSwapsPreview(applied);
  }, [brokers]);

  const onSwapsDrop = async (e) => {
    e.preventDefault();
    setSwapsDrag(false);
    await onSwapsFile(e.dataTransfer.files?.[0]);
  };

  const downloadSwaps = () => {
    const catalog = buildSwapsCatalog(brokers);
    const stamp = new Date().toISOString().slice(0, 10);
    downloadJson(`swaps-catalog-${stamp}.json`, catalog);
    toast.success(`Downloaded ${catalog.brokers.length} brokers’ swap data`);
  };

  const applySwaps = async () => {
    if (!swapsPreview?.updated?.length) return;
    setSwapsBusy(true);
    try {
      const res = await bulkUpsertBrokers(swapsPreview.updated);
      toast.success(`Updated swaps for ${res.count || swapsPreview.updated.length} brokers`);
      setSwapsPreview(null);
      setSwapsError("");
      if (swapsInputRef.current) swapsInputRef.current.value = "";
    } catch (err) {
      toast.error(err.message || "Swap update failed");
    } finally {
      setSwapsBusy(false);
    }
  };

  return (
    <div className="space-y-8" data-testid="admin-swaps-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div className="flex items-start gap-3">
          <div className="mt-1 flex h-10 w-10 items-center justify-center rounded-xl bg-[#0052ff]/15 text-[#60a5fa]">
            <ArrowLeftRight size={18} />
          </div>
          <div>
            <p className="admin-mono text-[11px] text-[#64748b]">Market data</p>
            <h1 className="mt-1 text-[36px] font-light tracking-[-0.045em] text-white">Swaps & compare</h1>
            <p className="mt-2 max-w-xl text-sm leading-relaxed text-[#94a3b8]">
              Download live swap values, edit offline, then upload to refresh /swaps, profiles, and compare.
            </p>
          </div>
        </div>
        <button type="button" className="admin-btn admin-btn-primary" onClick={downloadSwaps} data-testid="admin-download-swaps">
          <Download size={14} /> Download current swaps JSON
        </button>
      </div>

      <motion.section
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        className="admin-panel p-6"
        data-testid="admin-swaps-panel"
      >
        <div className="mb-5">
          <h2 className="text-lg font-medium tracking-[-0.02em] text-white">Upload edited swaps</h2>
          <p className="mt-1 text-sm text-[#94a3b8]">
            Same shape as the download. Patches apply by slug onto existing brokers.
          </p>
        </div>

        <div className="grid gap-5 lg:grid-cols-2">
          <div
            className="admin-dropzone"
            data-active={swapsDrag}
            onDragOver={(e) => { e.preventDefault(); setSwapsDrag(true); }}
            onDragLeave={() => setSwapsDrag(false)}
            onDrop={onSwapsDrop}
            onClick={() => swapsInputRef.current?.click()}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") swapsInputRef.current?.click(); }}
            data-testid="admin-swaps-dropzone"
          >
            <UploadCloud className="mx-auto mb-3 text-[#14b8a6]" size={26} />
            <p className="text-sm font-medium text-white">Drop edited swaps JSON</p>
            <p className="mt-1 text-xs text-[#64748b]">same shape as the download · patches by slug</p>
            <input
              ref={swapsInputRef}
              type="file"
              accept="application/json,.json"
              className="hidden"
              data-testid="admin-swaps-input"
              onChange={(e) => onSwapsFile(e.target.files?.[0])}
            />
          </div>

          <div className="rounded-2xl border border-white/10 bg-[#020617]/50 p-4">
            <p className="admin-mono text-[10px] text-[#64748b]">Workflow</p>
            <ol className="mt-3 space-y-2 text-sm text-[#cbd5e1]">
              <li>1. Download current swaps JSON</li>
              <li>2. Edit <span className="admin-mono text-[#93c5fd]">swaps</span>, timestamps, swap-free flags</li>
              <li>3. Upload here → review → apply to site</li>
            </ol>
            <p className="mt-4 text-xs leading-relaxed text-[#64748b]">
              Seed brokers become overrides when updated. Unknown slugs are skipped. Live catalog: {brokers.length} brokers.
            </p>
          </div>
        </div>

        {swapsError ? (
          <p className="mt-4 rounded-xl border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-sm text-amber-200" role="alert">
            {swapsError}
          </p>
        ) : null}

        {swapsPreview ? (
          <div className="mt-5 space-y-4">
            <div className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-teal-500/25 bg-teal-500/10 px-4 py-3">
              <div>
                <p className="text-sm font-medium text-white">
                  Ready to update {swapsPreview.updated.length} broker{swapsPreview.updated.length === 1 ? "" : "s"}
                </p>
                <p className="admin-mono mt-1 text-[11px] text-[#5eead4]">
                  {swapsPreview.updated.map((b) => b.slug).join(", ")}
                </p>
              </div>
              <button
                type="button"
                className="admin-btn admin-btn-primary !bg-teal-600 hover:!bg-teal-500"
                disabled={swapsBusy}
                onClick={applySwaps}
                data-testid="admin-apply-swaps"
              >
                {swapsBusy ? "Applying…" : "Apply swap updates"}
              </button>
            </div>
            <pre className="admin-preview admin-mono" data-testid="admin-swaps-preview">
              {JSON.stringify(
                swapsPreview.updated.map((b) => ({
                  slug: b.slug,
                  name: b.name,
                  swapSource: b.swapSource,
                  swapUpdated: b.swapUpdated,
                  swapFree: b.swapFree,
                  tripleSwapDay: b.tripleSwapDay,
                  swaps: b.swaps,
                })),
                null,
                2,
              )}
            </pre>
          </div>
        ) : null}
      </motion.section>
    </div>
  );
}
